import { OrderResultsReducerChangeLastOrderAction, OrderResultsReducerNewOrderPendingAction } from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { EMPTY, merge, Observable, of } from 'rxjs'
import { mergeMap } from 'rxjs/operators'

import { OrderFailedResult, OrderResultWithPendingStatus, OrderSuccessResultWithMeta } from 'components/features/rewards/rewardsPane/types/orderResult'
import {
  CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_ENABLE,
  CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_SENDING_REQUEST,
  CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_SUCCESS,
} from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { EpicRefined } from 'interfaces/middleware'
import { ajaxCatch, AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'
import { CryptoRewardsPaneActions } from 'components/features/rewards/rewardsPane/store/actions'
import { byType } from 'rxOperators/byType'
import {
  createRewardsPaneActionChangeAutoInvestmentsModeStepState,
  SuccessCryptoRewardsPaneModeStepPayload,
  SuccessCryptoRewardsAutoInvestmentsModeStepAction,
} from 'components/features/rewards/rewardsPane/store/actions/paneState'
import { FLOATING_PANE_AUTO_INVESTMENTS_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { POST_CHANGE_AUTOINVESTMENTS_IN_CRYPTO_MODE_URL } from 'components/features/rewards/rewardsPane/constants/endpoints'
import { ModifyFloatingPane } from 'actions/floatingPanesActions'
import { CryptoRewardsChangeAutoinvestmentsModeRequest } from 'components/features/rewards/rewardsPane/types/apiRequests'
import { CryptoRewardsAutoInvestmentsModeStateChangeFailedResponse } from 'components/features/rewards/rewardsPane/types/apiResponses'
import { CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR } from 'components/features/rewards/rewardsPane/constants/defaults'
import BountyActions, { SetRewardsCryptoInvestmentsStateAction, SetRewardsCryptoInvestmentsStateActionPayload } from 'actions/bountyActions'
import size from 'lodash/size'

type PaneAutoinvestmentsMode = typeof FLOATING_PANE_AUTO_INVESTMENTS_MODE

export const enableAutoinvestmentsModeEpic: EpicRefined<
  | CryptoRewardsPaneActions
  | ModifyFloatingPane
  | BountyActions
  | AjaxCatchExtraActions
> = (
  (action$, _, { post }) => action$
    .pipe(
      byType(SuccessCryptoRewardsAutoInvestmentsModeStepAction.type),
      mergeMap(
        ({ payload }) => {
          if (payload.step !== CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_ENABLE) {
            return EMPTY
          }

          const { fields }: SuccessCryptoRewardsPaneModeStepPayload<PaneAutoinvestmentsMode> = payload

          if (!fields) {
            throw new Error('No order data')
          }

          const {
            cryptoCurrencyIDChosenForInvestment,
          } = fields

          const actionOpenSendingOrderStep: ModifyFloatingPane = (
            createRewardsPaneActionChangeAutoInvestmentsModeStepState({
              step: CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_SENDING_REQUEST,
              progressPercents: 70,
              fields: {
                cryptoCurrencyIDChosenForInvestment,
              },
            })
          )
          const actionOpenSendingOrderStepObservable: Observable<ModifyFloatingPane> = of(actionOpenSendingOrderStep)
          const actionResetLastOrderResultObservable = of(
            new OrderResultsReducerNewOrderPendingAction(FLOATING_PANE_AUTO_INVESTMENTS_MODE)
          )

          const sendOrderParams: CryptoRewardsChangeAutoinvestmentsModeRequest = {
            autoinvest_ticker: cryptoCurrencyIDChosenForInvestment,
            is_autoinvest_on: true,
          }
          const sendOrderObservable = post(POST_CHANGE_AUTOINVESTMENTS_IN_CRYPTO_MODE_URL, {
            body: sendOrderParams,
          }).pipe(
            mergeMap(() => {
              const orderSuccessResult: OrderSuccessResultWithMeta<PaneAutoinvestmentsMode> = {
                mode: FLOATING_PANE_AUTO_INVESTMENTS_MODE,
                currencyId: cryptoCurrencyIDChosenForInvestment,
              }
              const orderResultWithStatus: OrderResultWithPendingStatus<PaneAutoinvestmentsMode> = {
                mode: FLOATING_PANE_AUTO_INVESTMENTS_MODE,
                isPending: false,
                success: orderSuccessResult,
                errors: undefined,
              }
              const rewardsAccountUpdatedState: SetRewardsCryptoInvestmentsStateActionPayload = {
                is_autoinvest_on: true,
                autoinvest_ticker: cryptoCurrencyIDChosenForInvestment,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastOrderAction(orderResultWithStatus)
              const actionChangeUserRewardsState = new SetRewardsCryptoInvestmentsStateAction(rewardsAccountUpdatedState)
              const actionChangePaneStep = createRewardsPaneActionChangeAutoInvestmentsModeStepState({
                step: CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_SUCCESS,
                progressPercents: 100,
                progressLabel: undefined,
                fields,
              })
              return merge(
                of(actionSetOrderResult),
                of(actionChangeUserRewardsState),
                of(actionChangePaneStep),
              )
            }),
            ajaxCatch((error) => {
              const {
                autoinvest_ticker,
                is_autoinvest_on,
                non_field_errors,
              }: CryptoRewardsAutoInvestmentsModeStateChangeFailedResponse = Boolean(size(error.response))
                ? error.response
                : CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR
              const fieldsErrors = {
                autoinvest_ticker,
                is_autoinvest_on,
              }
              const requestFailedErrors: OrderFailedResult<PaneAutoinvestmentsMode> = {
                fieldsErrors: fieldsErrors,
                generalErrors: non_field_errors,
              }
              const enableAutoInvestmentsModeWithStatus: OrderResultWithPendingStatus<PaneAutoinvestmentsMode> = {
                mode: FLOATING_PANE_AUTO_INVESTMENTS_MODE,
                isPending: false,
                errors: requestFailedErrors,
                success: undefined,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastOrderAction(enableAutoInvestmentsModeWithStatus)
              const actionChangePaneStep = createRewardsPaneActionChangeAutoInvestmentsModeStepState({
                step: CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_ENABLE,
                progressPercents: 20,
                fields,
              })
              return merge(
                of(actionSetOrderResult),
                of(actionChangePaneStep),
              )
            })
          )
          return merge(
            actionOpenSendingOrderStepObservable,
            actionResetLastOrderResultObservable,
            sendOrderObservable
          )
        }
      )
    )
)
